import { NodeRect } from "./ownershipEdgeRouting.helpers";

// Clearance kept between two node cards; a dragged node is pushed out until it
// sits at least this far from every other node (one grid step).
const COLLISION_GAP = 20;
const MAX_COLLISION_ITERATIONS = 8;

/** True when rect `a` comes within `gap` of rect `b` (overlap incl. clearance). */
export function overlapsWithGap(
    a: NodeRect,
    b: NodeRect,
    gap: number,
): boolean {
    return (
        a.x < b.x + b.width + gap &&
        a.x + a.width + gap > b.x &&
        a.y < b.y + b.height + gap &&
        a.y + a.height + gap > b.y
    );
}

export interface CollisionBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

/**
 * Nudges the active node out of any overlap with the others by the smallest
 * axis-aligned move (minimum translation), keeping `gap` clearance. With
 * `bounds` set (e.g. a group box), every escape is kept inside those bounds —
 * so an escape the caller would later clamp back onto a neighbour is scored as
 * still-overlapping and loses to one that stays clear (a member in a one-row
 * box then escapes sideways, not up/down into the clamp). Iterated a few times
 * because escaping one neighbour can push into another. Pure — the separation
 * math is unit-tested without the canvas.
 */
export function resolveNodeCollision({
    active,
    others,
    gap = COLLISION_GAP,
    bounds,
}: {
    active: NodeRect;
    others: NodeRect[];
    gap?: number;
    bounds?: CollisionBounds;
}): { x: number; y: number } {
    const { width, height } = active;
    const clampToBounds = (point: { x: number; y: number }) =>
        bounds
            ? {
                  x: Math.min(Math.max(point.x, bounds.minX), bounds.maxX),
                  y: Math.min(Math.max(point.y, bounds.minY), bounds.maxY),
              }
            : point;
    const overlapCountAt = (x: number, y: number): number =>
        others.filter((other) =>
            overlapsWithGap({ x, y, width, height }, other, gap),
        ).length;

    let { x, y } = clampToBounds(active);
    for (let iteration = 0; iteration < MAX_COLLISION_ITERATIONS; iteration++) {
        const hit = others.find((other) =>
            overlapsWithGap({ x, y, width, height }, other, gap),
        );
        if (!hit) {
            break;
        }
        // Four ways out of this neighbour, clamped into bounds; pick the one
        // that clears the most overlaps (avoids oscillating when sandwiched),
        // nearest first.
        const candidates = [
            { x: hit.x - gap - width, y }, // left
            { x: hit.x + hit.width + gap, y }, // right
            { x, y: hit.y - gap - height }, // up
            { x, y: hit.y + hit.height + gap }, // down
        ].map(clampToBounds);
        const [first, ...rest] = candidates;
        const best = rest.reduce((chosen, candidate) => {
            const chosenOverlaps = overlapCountAt(chosen.x, chosen.y);
            const candidateOverlaps = overlapCountAt(candidate.x, candidate.y);
            if (candidateOverlaps !== chosenOverlaps) {
                return candidateOverlaps < chosenOverlaps ? candidate : chosen;
            }
            const chosenDist = Math.hypot(chosen.x - x, chosen.y - y);
            const candidateDist = Math.hypot(candidate.x - x, candidate.y - y);
            return candidateDist < chosenDist ? candidate : chosen;
        }, first);
        // No move improved things (fully boxed in) — stop rather than spin.
        if (best.x === x && best.y === y) {
            break;
        }
        x = best.x;
        y = best.y;
    }
    return { x, y };
}
